import {
  Alert, Button, Form, Modal, Space,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { LeftOutlined } from '@ant-design/icons';
import ReCAPTCHA from 'react-google-recaptcha';
import { useMediaQuery } from 'react-responsive';
import { ContainerWithForm } from '../../components/Container';
import { RequestStatus } from '../../model';
import { addBookingQuestionAndAnswersData, resetBookingData, selectBookingData } from '../../redux/slices/booking';
import { useAppDispatch } from '../../hooks/redux-hooks';
import { bookingAppointment } from '../../services/booking';
import { BookingConfirmPanel } from './BookingConfirmModal';
import { DOCTORS_ROUTE, PAYMENT, THANKS } from '../../constants/routes';
import { useGetSpecialistsQuery } from '../../services/specialists';
import { generateSpecialities } from '../../utils/specialist';
import analytics from '../../infra/analytics';
import {
  CONFIRM_BOOKING, SITE_NAME,
} from '../../constants/analytics';
import { StylePageSubtitle } from '../../styles/sharedStyleComponets';
import { QUERIES } from '../../styles/breakpoint';

const Finish = () => {
  const isMobile = useMediaQuery({ query: QUERIES.mobile });
  const bookingData = useSelector(selectBookingData);
  const appointment = bookingData?.appointment;

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const dispatch = useAppDispatch();
  const [bookingStatus, setBookingStatus] = useState(RequestStatus.DEFAULT);
  const { data: specialists } = useGetSpecialistsQuery(bookingData.speciality?.id || '');
  const specialist = specialists?.find((_specialist) => _specialist.id === appointment?.specialistId);
  const [isRobot, setIsRobot] = useState(true);

  useEffect(() => {
    analytics.logPageViewEvent(CONFIRM_BOOKING, {
      site: SITE_NAME,
      page_path: pathname,
    });
  }, [pathname]);

  const handleRobotCheckboxChange = useCallback((value) => setIsRobot(!value), [setIsRobot]);

  const showTimeSlotConflictModal = useCallback(() => {
    Modal.warning({
      title: 'This time slot is no longer available',
      content: 'Sorry, the appointment time you selected has just been booked by someone else. '
        + 'Please go back and choose another available time.',
      okText: 'Choose another time',
      centered: true,
      onOk: () => navigate(`${DOCTORS_ROUTE}${search}`),
    });
  }, [navigate, search]);

  const handleSubmit = useCallback(async (): Promise<RequestStatus> => {
    setBookingStatus(RequestStatus.LOADING);

    analytics.trackVpcFormSubmit({
      form_id: bookingData.formId,
      page_path: pathname,
      timestamp: moment().tz('Australia/Melbourne').unix(),
    });

    let values = {};
    const bookingDataBody = { ...bookingData };
    try {
      values = await form.validateFields();
      const newQuestionAndAnswersData = { ...values, consultWay: 'video' };
      dispatch(addBookingQuestionAndAnswersData(newQuestionAndAnswersData));
      bookingDataBody.questionAndAnswers = { ...bookingData.questionAndAnswers, ...newQuestionAndAnswersData };
      const response = await bookingAppointment(bookingDataBody);
      setBookingStatus(response.status);

      // Track booking success event
      if (response.status === RequestStatus.SUCCESS && response.data) {
        analytics.trackVpcBookingSuccess({
          booking_id: response.data.toString(),
          timestamp: moment().tz('Australia/Melbourne').unix(),
        });
      } else {
        analytics.trackVpcFormError({
          form_id: bookingData.formId,
          error_type: 'book_appointment_failed',
          page_path: pathname,
        });
      }

      return response.status;
    } catch (error) {
      setBookingStatus(RequestStatus.FAILURE);
      analytics.trackVpcFormError({
        form_id: bookingData.formId,
        error_type: 'server_error',
        page_path: pathname,
      });

      return RequestStatus.FAILURE;
    }
  }, [bookingData, pathname, form, dispatch]);

  return (
    <ContainerWithForm>
      <BookingConfirmPanel
        bookingSummary={{
          specialist: {
            avatarURL: specialist?.avatarLink || '',
            title: specialist?.title || '',
            displayName: specialist ? `${specialist.firstName} ${specialist.lastName}` : '',
            specialityInfo: generateSpecialities(specialist?.specialities),
          },
          appointment: {
            startDateTime: `${
              moment(appointment?.date, 'YYYY-MM-DD').format('dddd, DD MMMM')
            } at ${
              moment(appointment?.startTime, 'hh:mm').format('hh:mm A')
            }`,
          },
        }}
      />
      <ReCAPTCHA
        // @ts-ignore
        style={{ width: isMobile ? '100%' : '300px', margin: '30px auto' }}
        sitekey={process.env.REACT_APP_RECAPTCHA_KEY as string}
        onChange={handleRobotCheckboxChange}
      />

      <div style={{ width: isMobile ? '100%' : '500px', margin: '30px auto' }}>
        <StylePageSubtitle>
          If you need to contact us prior or after your consultation, please find our contact details:
          <br />
          Phone: 03 7023 2883
          <br />
          Email:
          {' '}
          <a href="mailto:admin@telecareonline.com.au"> amplarhealthpsychology@telecare.com.au</a>
        </StylePageSubtitle>
      </div>

      <Space size="large" style={{ marginTop: '50px' }}>
        <Link to={`${PAYMENT}${search}`}>
          <LeftOutlined />
          Back
        </Link>
        <Button
          disabled={bookingStatus === RequestStatus.SUCCESS || isRobot}
          shape="round"
          type="primary"
          onClick={async () => {
            const status = await handleSubmit();
            if (status === RequestStatus.SUCCESS) {
              navigate(THANKS);
              dispatch(resetBookingData());
            } else if (status === RequestStatus.CONFLICT) {
              showTimeSlotConflictModal();
            }
          }}
          loading={bookingStatus === RequestStatus.LOADING}
        >
          Book
        </Button>
      </Space>
      {
        [RequestStatus.FAILURE, RequestStatus.SUCCESS].includes(bookingStatus) && (
        <Alert
          style={{ marginTop: '20px' }}
          message={`Booking ${bookingStatus === RequestStatus.SUCCESS ? 'success' : 'failure'}`}
          type={bookingStatus === RequestStatus.SUCCESS ? 'success' : 'error'}
        />
        )
      }
    </ContainerWithForm>
  );
};

export default Finish;
